import { useState, useEffect } from "react";
import { Box, Button, Chip, Typography, Paper } from "@mui/material";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { SupplieGrid } from "./SupplieGrid";
import { colors as color } from "../../ui/colors";


export function SuppliesProvider({ jobServiceId, selectedSupplies = [], setAllSuppliesForJob, jobServiceName }) {
  const [localSelectedSupplies, setLocalSelectedSupplies] = useState(selectedSupplies);

  useEffect(() => {
    setAllSuppliesForJob(jobServiceId, localSelectedSupplies);
  }, [localSelectedSupplies, jobServiceId]);

  const handleRemove = (id) => {
    setLocalSelectedSupplies((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <BaseModalComponent
        modalLabel="Supplies"
        modalKey="Supplies"
        elementTrigger={
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
            <Button
              variant="contained"
              sx={{
                py: 1.2,
                px: 3,
                mt: 2,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                backgroundColor: color.orange?.base || '#FB8E00',
                boxShadow: `0 2px 4px ${color.orange?.[200]}80`,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: color.orange?.[600] || '#E07D00',
                  boxShadow: `0 4px 8px ${color.orange?.[300]}80`,
                },
              }}
            >
              + Add Supplies
            </Button>
          </Box>
        }
      >
        {localSelectedSupplies && <SupplieGrid selectedSupplies={localSelectedSupplies} setSelectedSupplies={setLocalSelectedSupplies} />}
      </BaseModalComponent>

      <Box sx={{ mt: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: color.gray?.[700] || 'var(--fx-surface-textsecondary, #64748B)',
            fontWeight: 600,
            mb: 1.5,
            letterSpacing: 0.3
          }}
        >
          Selected Supplies ({localSelectedSupplies.length})
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            borderRadius: 2,
            border: localSelectedSupplies.length
              ? `2px solid ${color.orange?.[100] || '#FFE0B8'}`
              : `2px dashed ${color.gray?.[200] || '#C2C2C2'}`,
            bgcolor: localSelectedSupplies.length
              ? color.orange?.[50] || '#FFF8F0'
              : color.gray?.[50] || '#F5F5F5',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {localSelectedSupplies.length === 0 ? (
            <Box sx={{
              width: '100%',
              textAlign: 'center',
              py: 2
            }}>
              <Typography
                variant="body2"
                sx={{
                  color: color.gray?.[400] || 'var(--fx-surface-textsecondary, #64748B)',
                  fontStyle: 'italic'
                }}
              >
                No supplies selected yet. Click "Add Supplies" to get started.
              </Typography>
            </Box>
          ) : (
            localSelectedSupplies?.map((supplie) => (
              <Chip
                key={supplie.id}
                label={supplie.name}
                size="medium"
                clickable
                onDelete={() => handleRemove(supplie.id)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: 2,
                  px: 1,
                  height: 36,
                  backgroundColor: color.orange?.base || '#FB8E00',
                  color: 'white',
                  border: `1px solid ${color.orange?.[600] || '#E07D00'}`,
                  transition: 'all 0.2s ease-in-out',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(var(--fx-surface-basergb, 255, 255, 255), 0.7)',
                    '&:hover': {
                      color: 'white',
                    },
                  },
                  "&:hover": {
                    backgroundColor: color.orange?.[600] || '#E07D00',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 8px ${color.orange?.[300]}60`,
                  },
                }}
              />
            ))
          )}
        </Paper>
      </Box>
    </Box>
  );
}
